import { useTranslation } from 'react-i18next';
import { BoundedContent, Box, FlexBox, FluidImage, H2, tokens, Typography } from '@vp/swan';
import EditWrapper from '../../../../components/EditWrapper/EditWrapper';
import { sanitiseLocale } from '../../../../i18n/config';
import { useTrackNavigationClicked } from '../../../../utils/analytics';
import { logomakerCtaPathByLocale } from '../../../../utils/urls';
import LogomakerCTA from '../LogomakerCTA/LogomakerCTA';

const HowItWorks = () => {
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const howItWorksSteps = [
        {
            title: t('Logomaker landing page > How it works.productTiles.0.productTileHeader'),
            subtitle: t(
                'Logomaker landing page > How it works.productTiles.0.productTileSubHeader',
            ),
            image: t('Logomaker landing page > How it works.productTiles.0.productImage.src'),
            alt: t('Logomaker landing page > How it works.productTiles.0.productImage.altText'),
        },
        {
            title: t('Logomaker landing page > How it works.productTiles.1.productTileHeader'),
            subtitle: t(
                'Logomaker landing page > How it works.productTiles.1.productTileSubHeader',
            ),
            image: t('Logomaker landing page > How it works.productTiles.1.productImage.src'),
            alt: t('Logomaker landing page > How it works.productTiles.1.productImage.altText'),
        },
        {
            title: t('Logomaker landing page > How it works.productTiles.2.productTileHeader'),
            subtitle: t(
                'Logomaker landing page > How it works.productTiles.2.productTileSubHeader',
            ),
            image: t('Logomaker landing page > How it works.productTiles.2.productImage.src'),
            alt: t('Logomaker landing page > How it works.productTiles.2.productImage.altText'),
        },
    ];

    const howToStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: t('Logomaker landing page > How it works > Structured data name.value'),
        step: howItWorksSteps.map((step) => ({
            '@type': 'HowToStep',
            name: step.title,
            text: step.subtitle,
            image: step.image,
        })),
    };

    const logomakerCTARef = useTrackNavigationClicked(
        'Design Services',
        'How It Works Section CTA',
        logomakerCtaPathByLocale(locale),
        'logoMaker',
    );

    return (
        <BoundedContent overflow="hidden" bgc="strong">
            <Box marginY="between-sections">
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(howToStructuredData),
                    }}
                />
                <EditWrapper url={t('Logomaker landing page > How it works.editUrl')}>
                    <H2 fontSkin="title-section">
                        {t('Logomaker landing page > How it works.sectionHeader')}
                    </H2>
                    <Typography marginBottom="between-subsections">
                        {t('Logomaker landing page > How it works.sectionSubHeader')}
                    </Typography>

                    <FlexBox
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        marginBottom="to-actions"
                        gap={6}
                    >
                        {howItWorksSteps.map((step, index) => (
                            <HowItWorksStep
                                title={step.title}
                                subtitle={step.subtitle}
                                src={step.image}
                                alt={step.alt}
                                key={index}
                            />
                        ))}
                    </FlexBox>
                </EditWrapper>
                <FlexBox justifyContent="center">
                    <LogomakerCTA ctaRef={logomakerCTARef} />
                </FlexBox>
            </Box>
        </BoundedContent>
    );
};

type HowItWorksStepProps = {
    title: string;
    subtitle: string;
    src: string;
    alt: string;
};

const HowItWorksStep = ({ title, subtitle, src, alt }: HowItWorksStepProps) => {
    return (
        <FlexBox style={{ flex: 1 }} flexDirection="column">
            <FluidImage
                src={src}
                alt={alt}
                style={{ borderRadius: tokens.SwanSemBorderRadiusStrong }}
            />
            <Typography fontSkin="title-subsection" marginY="between-actions">
                {title}
            </Typography>
            <Typography>{subtitle}</Typography>
        </FlexBox>
    );
};

export default HowItWorks;
